import React, { useEffect, useState } from 'react';
import { Check, NotepadText, Pencil, X } from 'lucide-react';
import { updateContactNote } from '../services/boardChatService';

const NOTE_MAX = 2000;

/**
 * Nota livre no contato Chatwoot (custom_attributes.nota).
 * Persiste entre conversas/atendimentos do mesmo cliente.
 */
export function BoardChatContactNote({
  contactId,
  note = '',
  onSaved,
  tipProps,
  hideTip,
  readOnly = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEditing(false);
    setDraft(note);
    setError('');
    setSaving(false);
  }, [contactId, note]);

  if (!contactId) return null;

  const hasNote = Boolean(String(note || '').trim());

  const startEdit = () => {
    if (readOnly) return;
    setDraft(note || '');
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(note || '');
    setError('');
    setEditing(false);
  };

  const save = async () => {
    const next = String(draft || '').trim().slice(0, NOTE_MAX);
    if (next === String(note || '').trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await updateContactNote(contactId, next);
      const saved = String(res?.data?.note ?? next).trim();
      onSaved?.(saved, res?.data?.contact || null);
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Não foi possível salvar a nota.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="bch-contact-note bch-contact-note--editing">
        <textarea
          id={`bch-note-${contactId}`}
          className="bch-contact-note-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
          disabled={saving}
          maxLength={NOTE_MAX}
          rows={3}
          placeholder="Observação que acompanha o cliente entre atendimentos…"
          aria-label="Anotações deste cliente"
          autoFocus
        />
        <div className="bch-contact-note-actions">
          <button
            type="button"
            className="bch-edit-btn bch-edit-btn--ok"
            onClick={save}
            disabled={saving}
            title="Salvar anotação"
            aria-label="Salvar anotação"
          >
            <Check size={14} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="bch-edit-btn"
            onClick={cancelEdit}
            disabled={saving}
            title="Cancelar"
            aria-label="Cancelar edição da anotação"
          >
            <X size={14} strokeWidth={2} />
          </button>
          <span className="bch-contact-note-count">
            {String(draft || '').length}/{NOTE_MAX}
          </span>
        </div>
        {error ? <span className="bch-details-edit-error">{error}</span> : null}
      </div>
    );
  }

  if (!hasNote) {
    if (readOnly) return null;
    return (
      <div className="bch-contact-note">
        <button
          type="button"
          className="bch-contact-note-tag bch-contact-note-tag--add"
          onClick={startEdit}
          aria-label="Adicionar anotação"
          {...(tipProps ? tipProps('Anotações deste cliente') : {})}
        >
          <NotepadText size={14} strokeWidth={2} aria-hidden="true" />
          <span className="bch-contact-note-tag-label bch-contact-note-tag-label--muted">
            adicionar anotação
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="bch-contact-note">
      <span
        className="bch-contact-note-tag"
        {...(tipProps ? tipProps('Anotações deste cliente') : {})}
      >
        <span className="bch-contact-note-tag-label">{note}</span>
        {!readOnly ? (
          <button
            type="button"
            className="bch-contact-note-tag-edit"
            onClick={startEdit}
            onMouseEnter={() => hideTip?.()}
            onFocus={() => hideTip?.()}
            aria-label="Editar anotação"
          >
            <Pencil size={11} strokeWidth={2.2} />
          </button>
        ) : null}
      </span>
    </div>
  );
}
